import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Plus, ChevronRight, Trash2, Pencil, Video,
  ClipboardList, Sparkles, Users, BarChart3,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { RunPracticeModal } from '@/components/coach-lab/RunPractice';
import { useIsDemo } from '@/context/DemoContext';
import { useDemoGuard } from '@/components/DemoGuard';

/* ── Design tokens ── */
const C = {
  orange: '#F5A623', navy: '#0D1B26', navyCard: '#1A2B3C',
  green: '#27AE60', red: '#E74C3C', blue: '#2980B9',
  muted: '#8FA3B1', textSec: '#A8BFCE', border: '#DDE5EC',
  trackGame: '#CE1141',
  runPractice: '#198754',
  manageRoster: '#0056B3',
  stats: '#0B1824',
};

const RECENT_WINDOW = 5;

export default function CoachDashboard() {
  const {
    user, teams, selectedTeamId, setSelectedTeamId,
    isStaff, setShowCreateTeamDialog,
  } = useOutletContext();
  const isDemo = useIsDemo();
  const demoBlocked = useDemoGuard();

  const [games, setGames] = useState([]);
  const [teamStats, setTeamStats] = useState(null);
  const [showRunPracticeModal, setShowRunPracticeModal] = useState(false);

  const navigate = useNavigate();

  const completedGames = useMemo(
    () =>
      games
        .filter(g => g.status === 'final' || g.status === 'locked')
        .sort((a, b) => new Date(b.date_time) - new Date(a.date_time)),
    [games]
  );
  const recentCompletedGames = completedGames.slice(0, 3);

  /* ── data fetching (chronological — no season filter) ── */
  useEffect(() => {
    if (selectedTeamId) {
      fetchGames();
      fetchTeamStats();
    } else {
      setGames([]);
      setTeamStats(null);
    }
  }, [selectedTeamId]);

  const fetchGames = async () => {
    try {
      const r = await api.get(`/teams/${selectedTeamId}/games`);
      setGames(r.data);
    } catch (e) { console.error('Failed to fetch games:', e); }
  };

  const fetchTeamStats = async () => {
    try {
      const r = await api.get(`/teams/${selectedTeamId}/stats`);
      setTeamStats(r.data);
    } catch { setTeamStats(null); }
  };

  /* ── Last N Games stats (chronological, season-agnostic) ── */
  const lastNStats = useMemo(() => {
    const history = teamStats?.game_history || [];
    if (history.length === 0) return null;
    const slice = history.slice(0, Math.min(RECENT_WINDOW, history.length));
    let wins = 0, losses = 0;
    let teamPts = 0, oppPts = 0;
    let fgm = 0, fga = 0;
    slice.forEach(g => {
      if (g.result === 'W') wins++;
      else if (g.result === 'L') losses++;
      const tScore = g.is_home ? g.home_score : g.away_score;
      const oScore = g.is_home ? g.away_score : g.home_score;
      teamPts += tScore || 0;
      oppPts += oScore || 0;
      fgm += g.stats?.fgm || 0;
      fga += g.stats?.fga || 0;
    });
    const n = slice.length;
    return {
      n,
      wins,
      losses,
      ppg: n > 0 ? teamPts / n : 0,
      oppPpg: n > 0 ? oppPts / n : 0,
      fgPct: fga > 0 ? (fgm / fga) * 100 : null,
    };
  }, [teamStats]);

  const handleDeleteGame = async (gameId, oppName) => {
    if (!window.confirm(`Permanently delete the game vs ${oppName}?`)) return;
    try { await api.delete(`/games/${gameId}`); toast.success(`Game vs ${oppName} deleted`); fetchGames(); fetchTeamStats(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete game'); }
  };

  const handleStartGame = async (gameId, e) => {
    e.stopPropagation();
    try {
      await api.patch(`/games/${gameId}`, { status: 'in_progress' });
      toast.success('Game started!');
      navigate(`/game/${gameId}`);
    } catch { toast.error('Failed to start game'); }
  };

  const selectedTeam = teams.find(t => t.team_id === selectedTeamId);

  /* ── No teams ── */
  if (teams.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          {isDemo && (
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg mb-6 mx-auto border max-w-md"
              style={{ background: '#FFF8E8', borderColor: '#F5A623' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
                <circle cx="12" cy="12" r="10" stroke="#F5A623" strokeWidth="2"/><path d="M12 16v-4M12 8h.01" stroke="#F5A623" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <p className="text-xs text-amber-700">The Coach Demo needs team data. An admin can seed demo data via <strong>Admin &rarr; Manage Coach Demo</strong>.</p>
            </div>
          )}
          <h1 className="text-4xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.navy }}>
            No Teams Yet
          </h1>
          <p className="text-[14px] mb-8" style={{ color: C.muted }}>
            {isStaff ? 'Coordinator support is no longer available — please contact your team manager.' : 'Create your first team to get started'}
          </p>
          {!isStaff && (
            <button
              onClick={() => {
                if (isDemo) {
                  demoBlocked('create and manage your own teams');
                  return;
                }
                // Open the top-drop-down Create Team modal owned by
                // CoachLayoutShell. The previous behavior navigated to
                // a full-page /onboarding/coach route with no exit.
                setShowCreateTeamDialog?.(true);
              }}
              data-testid="create-first-team-button"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-[15px] uppercase tracking-[.07em] transition-all hover:opacity-90"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", background: C.orange, color: C.navy, border: 'none' }}
            >
              <Plus className="w-5 h-5" />
              Create Team
            </button>
          )}
        </div>
      </div>
    );
  }

  const upcomingGames = games.filter(g => g.status === 'scheduled' || g.status === 'in_progress');
  const resumableGame = games.find(g => g.status === 'in_progress');

  /* ══════════════════════════════════════════
     MAIN CONTENT
  ══════════════════════════════════════════ */
  return (
    <>
      {/* ── RESUME-IN-PROGRESS BANNER ──
          Surfaces any `in_progress` game on the selected team so the
          coach can pick up exactly where they left off. */}
      {resumableGame && (
        <button
          onClick={() => navigate(`/game/${resumableGame.game_id}`)}
          data-testid="resume-game-banner"
          className="w-full mb-4 flex items-center justify-between gap-3 px-4 py-3 rounded-2xl active:scale-[.99] transition-transform text-left"
          style={{
            background: '#FFF3DA',
            border: '1.5px solid #ffa133',
            fontFamily: "'Barlow Condensed', sans-serif",
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: '#ffa133', color: '#0B1824' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-[.12em]" style={{ color: '#8a4f00' }}>
                Game in progress
              </div>
              <div className="text-[15px] font-bold uppercase truncate" style={{ color: '#0B1824' }}>
                vs {resumableGame.opponent_name || 'Opponent'} · {resumableGame.home_score ?? 0}–{resumableGame.away_score ?? 0}
              </div>
            </div>
          </div>
          <span
            className="flex-shrink-0 px-4 h-9 inline-flex items-center rounded-full text-[12px] font-bold uppercase tracking-[.08em]"
            style={{ background: '#0B1824', color: '#fff' }}
          >
            Resume
          </span>
        </button>
      )}

      {/* ── WELCOME + TEAM PICKER + RECORD (single row) ── */}
      <div className="mb-6" data-testid="welcome-row">
        <div className="text-[14px] font-bold tracking-[.08em] uppercase mb-0.5" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.navy }}>
          Welcome back
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h1 className="text-[32px] font-bold uppercase tracking-[.01em] leading-none whitespace-nowrap" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.navy }}>
            {user?.name || 'Coach'}
          </h1>
          <div className="flex-1 min-w-[160px] max-w-[280px]">
            <Select value={selectedTeamId || ''} onValueChange={(val) => {
              if (val === 'create_new') {
                if (demoBlocked('create and manage your own teams')) return;
                setSelectedTeamId('create_new');
              } else {
                setSelectedTeamId(val);
              }
            }}>
              <SelectTrigger
                data-testid="dashboard-team-selector"
                className="h-9 border-[1.5px] rounded-full gap-2 text-[15px] font-semibold uppercase tracking-[.07em] px-4"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", background: '#fff', borderColor: C.border, color: C.navy }}
              >
                <SelectValue placeholder="Select team..." />
              </SelectTrigger>
              <SelectContent>
                {teams.map(t => (
                  <SelectItem key={t.team_id} value={t.team_id}>
                    {t.name}{t.level ? ` (${t.level})` : ''}
                  </SelectItem>
                ))}
                {!isStaff && (
                  <SelectItem value="create_new" className="text-blue-600 font-bold border-t">
                    <span className="flex items-center"><Plus className="w-3.5 h-3.5 mr-1.5" />Create New Team</span>
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          {lastNStats && (lastNStats.wins + lastNStats.losses) > 0 && (
            <div
              className="h-9 px-3.5 rounded-full flex items-center gap-1.5 text-[15px] font-semibold uppercase tracking-[.07em]"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", background: '#fff', border: `1.5px solid ${C.border}`, color: C.navy }}
              data-testid="dashboard-record-badge"
            >
              <span style={{ color: '#198754' }}>{lastNStats.wins}</span>
              <span style={{ color: '#94a3b8' }}>–</span>
              <span style={{ color: '#CE1141' }}>{lastNStats.losses}</span>
              <span className="ml-1 text-[11px] font-semibold uppercase tracking-wider" style={{ color: C.muted }}>L{lastNStats.n}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── QUICK ACTIONS (2x2 grid — pill-shaped) ── */}
      <div className="grid grid-cols-2 gap-2.5 sm:gap-3 mb-7" data-testid="quick-actions">
        <button onClick={() => {
            navigate('/game/new', { state: { teamId: selectedTeamId } });
          }}
          data-testid="track-game-button"
          className="flex items-center justify-center gap-2 h-14 rounded-full text-[15px] font-semibold uppercase tracking-[.07em] text-white transition-all hover:opacity-90 active:scale-[.97]"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", background: C.trackGame, border: 'none' }}>
          <ClipboardList className="w-[16px] h-[16px]" /> Track Game
        </button>
        <button onClick={() => setShowRunPracticeModal(true)}
          data-testid="run-practice-button"
          className="flex items-center justify-center gap-2 h-14 rounded-full text-[15px] font-semibold uppercase tracking-[.07em] text-white transition-all hover:opacity-90 active:scale-[.97]"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", background: C.runPractice, border: 'none' }}>
          <Sparkles className="w-[16px] h-[16px]" /> Smart Practice
        </button>
        <button onClick={() => navigate('/teams')}
          data-testid="manage-roster-button"
          className="flex items-center justify-center gap-2 h-14 rounded-full text-[15px] font-semibold uppercase tracking-[.07em] text-white transition-all hover:opacity-90 active:scale-[.97]"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", background: C.manageRoster, border: 'none' }}>
          <Users className="w-[16px] h-[16px]" /> Manage Roster
        </button>
        <button onClick={() => navigate('/stats')}
          data-testid="stats-button"
          className="flex items-center justify-center gap-2 h-14 rounded-full text-[15px] font-semibold uppercase tracking-[.07em] text-white transition-all hover:opacity-90 active:scale-[.97]"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", background: C.stats, border: 'none' }}>
          <BarChart3 className="w-[16px] h-[16px]" /> Stats
        </button>
      </div>

      {/* ── LAST 5 GAMES — 4-tile compact panel ── */}
      <div className="flex items-center justify-between mb-3.5">
        <h2 className="text-[18px] font-extrabold uppercase tracking-[.08em] flex items-center gap-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.navy }}>
          {lastNStats?.n ? `Last ${lastNStats.n} Game${lastNStats.n > 1 ? 's' : ''}` : 'Last 5 Games'}
        </h2>
        <span className="text-[14px] font-medium" style={{ color: C.muted }}>Most recent</span>
      </div>
      {!lastNStats || lastNStats.n === 0 ? (
        <div className="rounded-xl p-6 mb-6 text-center" style={{ background: '#fff', border: `1.5px solid ${C.border}` }} data-testid="last-n-empty">
          <p className="text-[14px]" style={{ color: C.muted }}>No completed games yet — your last 5 games will show up here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-6" data-testid="last-n-stats-grid">
          {[
            {
              key: 'record',
              label: 'Record',
              value: `${lastNStats.wins}–${lastNStats.losses}`,
              sub: `Last ${lastNStats.n} game${lastNStats.n > 1 ? 's' : ''}`,
              testid: 'last-n-stat-record',
            },
            {
              key: 'ppg',
              label: 'PPG',
              value: lastNStats.ppg.toFixed(1),
              sub: 'Points per game',
              testid: 'last-n-stat-ppg',
            },
            {
              key: 'opp-ppg',
              label: 'Opp PPG',
              value: lastNStats.oppPpg > 0 ? lastNStats.oppPpg.toFixed(1) : '—',
              sub: 'Opponent points',
              testid: 'last-n-stat-opp-ppg',
            },
            {
              key: 'fg-pct',
              label: 'FG%',
              value: lastNStats.fgPct != null ? `${lastNStats.fgPct.toFixed(1)}%` : '—',
              sub: 'Field-goal %',
              testid: 'last-n-stat-fg-pct',
            },
          ].map(s => (
            <div key={s.key} className="rounded-[10px] p-4" style={{ background: '#fff', border: `1.5px solid ${C.border}` }} data-testid={s.testid}>
              <div className="text-[14px] font-bold tracking-[.16em] uppercase mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.muted }}>{s.label}</div>
              <div className="text-[28px] font-bold tracking-tight leading-none" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.navy }}>
                {s.value}
              </div>
              <div className="text-[14px] mt-1.5 font-medium" style={{ color: C.muted }}>{s.sub}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── RECENT PERFORMANCE ── */}
      {recentCompletedGames.length > 0 && (
        <div className="rounded-xl overflow-hidden mb-6" style={{ background: '#fff', border: `1.5px solid ${C.border}` }} data-testid="recent-performance-snapshot">
          <div className="px-5 py-3.5" style={{ borderBottom: '1px solid #EEF2F5' }}>
            <div className="flex items-center justify-between">
              <h2 className="text-[18px] font-extrabold uppercase tracking-[.08em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.navy }}>
                Recent Performance
              </h2>
              <button onClick={() => navigate('/games', { state: { teamId: selectedTeamId } })}
                className="flex items-center gap-1 text-[14px] font-bold uppercase tracking-[.08em]"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.blue, background: 'none', border: 'none', cursor: 'pointer' }}
                data-testid="view-all-games-link">
                View All <ChevronRight className="w-[13px] h-[13px]" />
              </button>
            </div>
          </div>
          {recentCompletedGames.map((game, idx) => {
            const isWin = (game.is_home && game.home_score > game.away_score) || (!game.is_home && game.away_score > game.home_score);
            const teamScore = game.is_home ? game.home_score : game.away_score;
            const oppScore = game.is_home ? game.away_score : game.home_score;
            return (
              <div key={game.game_id} onClick={() => navigate(`/game/${game.game_id}/summary`)}
                className="flex items-center gap-3.5 px-5 py-3.5 cursor-pointer transition-colors hover:bg-[#FAFCFE]"
                style={{ borderBottom: idx < recentCompletedGames.length - 1 ? '1px solid #F4F7FA' : 'none' }}
                data-testid={`recent-game-${game.game_id}`}>
                <div className="w-8 h-8 rounded-md flex items-center justify-center text-[14px] font-bold tracking-[.04em] shrink-0"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", background: isWin ? 'rgba(39,174,96,.12)' : 'rgba(231,76,60,.1)', color: isWin ? C.green : C.red }}>
                  {isWin ? 'W' : 'L'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold truncate" style={{ color: C.navy }}>vs {game.opponent_name}</div>
                  <div className="text-[14px] mt-0.5" style={{ color: C.muted }}>
                    {new Date(game.date_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {game.team_level && <span className="ml-1">&middot; {game.team_level}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {game.is_manual_entry && (
                    <button onClick={(e) => { e.stopPropagation(); navigate(`/manual-stats-entry/${game.game_id}`, { state: { editMode: true, teamId: selectedTeamId } }); }}
                      className="p-1 rounded" style={{ color: C.muted, background: 'none', border: 'none' }} title="Edit Stats" data-testid={`edit-game-${game.game_id}`}>
                      <Pencil className="w-[14px] h-[14px]" />
                    </button>
                  )}
                  {game.replay_url && (
                    <a href={game.replay_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                      className="p-1 rounded" style={{ color: C.blue }} title="Watch Replay" data-testid={`replay-link-${game.game_id}`}>
                      <Video className="w-[14px] h-[14px]" />
                    </a>
                  )}
                  <div className="text-[16px] font-bold tracking-[.04em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: isWin ? C.green : C.red }}>
                    {teamScore} – {oppScore}
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); handleDeleteGame(game.game_id, game.opponent_name); }}
                    className="p-1 rounded transition-colors hover:text-[#E74C3C]"
                    style={{ color: C.border, background: 'none', border: 'none', cursor: 'pointer' }} title="Delete Game" data-testid={`delete-game-${game.game_id}`}>
                    <Trash2 className="w-[15px] h-[15px]" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── UPCOMING GAMES ── */}
      {upcomingGames.length > 0 && (
        <div className="rounded-xl overflow-hidden mb-6" style={{ background: '#fff', border: `1.5px solid ${C.border}` }} data-testid="upcoming-games-section">
          <div className="px-5 py-3.5" style={{ borderBottom: '1px solid #EEF2F5' }}>
            <h2 className="text-[18px] font-extrabold uppercase tracking-[.08em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.navy }}>
              Upcoming Games
            </h2>
          </div>
          {upcomingGames
            .sort((a, b) => new Date(a.date_time) - new Date(b.date_time))
            .map((game, idx, arr) => (
              <div key={game.game_id}
                onClick={() => game.status === 'in_progress' && navigate(`/game/${game.game_id}`)}
                className={`flex items-center justify-between px-5 py-3.5 ${game.status === 'in_progress' ? 'cursor-pointer hover:bg-[#FAFCFE]' : ''}`}
                style={{ borderBottom: idx < arr.length - 1 ? '1px solid #F4F7FA' : 'none' }}
                data-testid={`upcoming-game-${game.game_id}`}>
                <div>
                  <div className="text-[14px] font-semibold" style={{ color: C.navy }}>vs {game.opponent_name}</div>
                  <div className="text-[14px] mt-0.5" style={{ color: C.muted }}>
                    {new Date(game.date_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} &middot; {game.is_home ? 'Home' : 'Away'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {game.status === 'scheduled' ? (
                    <button onClick={(e) => handleStartGame(game.game_id, e)} data-testid={`start-game-${game.game_id}`}
                      className="px-4 py-2 rounded-full text-[15px] font-semibold uppercase tracking-[.07em] text-white transition-all hover:opacity-90"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif", background: C.blue, border: 'none' }}>
                      Start
                    </button>
                  ) : (
                    <>
                      <span className="text-[16px] font-bold tracking-[.04em] tabular-nums" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.navy }}>
                        {game.is_home ? game.home_score : game.away_score} – {game.is_home ? game.away_score : game.home_score}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color: C.green, background: 'rgba(39,174,96,.12)' }}>LIVE</span>
                    </>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}

      {/* ── NO GAMES ── */}
      {games.length === 0 && selectedTeamId && (
        <div className="rounded-xl p-8 text-center" style={{ background: '#fff', border: `1.5px solid ${C.border}` }}>
          <p className="mb-4 text-[14px]" style={{ color: C.muted }}>No games scheduled yet</p>
          <button onClick={() => navigate('/game/new', { state: { teamId: selectedTeamId } })}
            className="px-5 py-2.5 rounded-full text-[15px] font-semibold uppercase tracking-[.07em] text-white transition-all hover:opacity-90"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", background: C.blue, border: 'none' }}>
            Schedule First Game
          </button>
        </div>
      )}

      {/* Run Practice Modal */}
      <RunPracticeModal
        open={showRunPracticeModal}
        onOpenChange={setShowRunPracticeModal}
        teams={teams}
        selectedTeamId={selectedTeamId}
        onStart={(practiceId) => navigate(`/coach/practice/run/${practiceId}`)}
      />
    </>
  );
}
